import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Package, Plus, Search, MoreHorizontal,
  Eye, Pencil, Trash2, QrCode, Power,
  FileSpreadsheet, ClipboardList, RefreshCw, Info,
} from 'lucide-react'
import {
  useCampanias,
  useCrearCampania,
  useActualizarCampania,
  useEliminarCampania,
  useToggleCampania,
  useDescargarQR,
} from '../hooks/useCampanias'
import { CampaniaFormDialog } from '../components/CampaniaFormDialog'
import { useTeam } from '@/shared/context/TeamContext'
import type { CampaniaCreatePayload, CampaniaUpdatePayload, CampaniaListItem } from '../types'

export function CampaniasListPage() {
  useDocumentTitle('Muestras Médicas')
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || ['admin', 'mm_admin'].some(g => user?.groups?.includes(g))

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  const [showInfo, setShowInfo] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [editingCampania, setEditingCampania] = useState<CampaniaListItem | null>(null)

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const areaGrupoId = isClientArea ? selectedArea?.laboratorio_id : undefined

  const { data, isLoading, refetch } = useCampanias({ page, page_size: 10, search: search || undefined, grupo_laboratorio_id: areaGrupoId ?? undefined })
  const crearMutation = useCrearCampania()
  const actualizarMutation = useActualizarCampania()
  const eliminarMutation = useEliminarCampania()
  const toggleMutation = useToggleCampania()
  const qrMutation = useDescargarQR()

  const handleSearch = () => {
    setPage(1)
    setSearch(searchInput)
  }

  const handleCreate = async (payload: CampaniaCreatePayload) => {
    await crearMutation.mutateAsync(payload)
    setIsCreateOpen(false)
  }

  const handleUpdate = async (payload: CampaniaUpdatePayload) => {
    if (!editingCampania) return
    await actualizarMutation.mutateAsync({ id: editingCampania.id, payload })
    setEditingCampania(null)
  }

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar esta campaña? Esta acción no se puede deshacer.')) {
      await eliminarMutation.mutateAsync(id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-6 w-6 text-black dark:text-white" />
            Muestras Médicas
          </h1>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="text-muted-foreground hover:text-foreground transition-colors ml-2"
          >
            <Info className="h-5 w-5" />
          </button>
          {showInfo && (
            <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
              Gestión de campañas de muestras
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          {isAdmin && (
            <Button size="sm" className="bg-teal-800 hover:bg-teal-900 text-white" onClick={() => setIsCreateOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nueva Campaña
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar campañas..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-9"
          />
        </div>
        <Button variant="outline" onClick={handleSearch}>Buscar</Button>
      </div>

      {/* Table */}
      <Card className="border border-gray-300 dark:border-gray-700">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : data && data.results.length > 0 ? (
          <div className="p-6">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableHead>Campaña</TableHead>
                  <TableHead className="text-center">Solicitudes</TableHead>
                  <TableHead className="text-center">Entregas</TableHead>
                  <TableHead className="text-center">% Conversión</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((campania) => (
                  <TableRow key={campania.id}>
                    <TableCell className="font-medium max-w-[250px] truncate">
                      {campania.titulo}
                    </TableCell>
                    <TableCell className="text-center">{campania.total_solicitudes}</TableCell>
                    <TableCell className="text-center">{campania.total_entregados}</TableCell>
                    <TableCell className="text-center">
                      {campania.total_solicitudes > 0 && campania.total_entregados != null
                        ? `${((campania.total_entregados / campania.total_solicitudes) * 100).toFixed(1)}%`
                        : '0%'}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(campania.created_at).toLocaleDateString('es-EC')}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={campania.activa ? 'badge-gradient' : ''} variant={campania.activa ? 'default' : 'secondary'}>
                        {campania.activa ? 'Activa' : 'Inactiva'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/muestras-medicas/${campania.id}`)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/muestras-medicas/${campania.id}`)}>
                            <Eye className="mr-2 h-4 w-4" /> Ver detalle
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/muestras-medicas/${campania.id}/solicitudes`)}>
                            <FileSpreadsheet className="mr-2 h-4 w-4" /> Solicitudes
                          </DropdownMenuItem>
                          {isAdmin && (
                            <DropdownMenuItem onClick={() => navigate(`/muestras-medicas/${campania.id}/form-builder`)}>
                              <ClipboardList className="mr-2 h-4 w-4" /> Form Builder
                            </DropdownMenuItem>
                          )}
                          {isAdmin && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setEditingCampania(campania)}>
                                <Pencil className="mr-2 h-4 w-4" /> Editar
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleMutation.mutate(campania.id)}>
                                <Power className="mr-2 h-4 w-4" />
                                {campania.activa ? 'Desactivar' : 'Activar'}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => qrMutation.mutate(campania.id)}>
                                <QrCode className="mr-2 h-4 w-4" /> Descargar QR
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive"
                                onClick={() => handleDelete(campania.id)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-muted-foreground">
                  Mostrando {(page - 1) * 10 + 1} - {Math.min(page * 10, data.count)} de {data.count} campañas
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                  >
                    Anterior
                  </Button>
                  <div className="text-sm">
                    Página {page} de {data.total_pages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === data.total_pages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-12 text-center">
            <Package className="mx-auto h-12 w-12 text-teal-500" />
            <h3 className="mt-4 text-lg font-semibold">No se encontraron campañas</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Crea una nueva campaña para comenzar
            </p>
          </div>
        )}
      </Card>

      {/* Create Dialog */}
      <CampaniaFormDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSubmit={handleCreate}
        isSubmitting={crearMutation.isPending}
        mode="create"
      />

      {/* Edit Dialog */}
      <CampaniaFormDialog
        open={!!editingCampania}
        onOpenChange={(open) => { if (!open) setEditingCampania(null) }}
        onSubmit={handleUpdate}
        isSubmitting={actualizarMutation.isPending}
        campania={editingCampania as any}
        mode="edit"
      />
    </div>
  )
}
